/**
 * On-device backup of survey free-text notes (localStorage), mirroring the
 * FSR localStorage-draft safety net. Notes autosave to Firestore, but the main
 * app uses an in-memory Firestore cache (no offline persistence), so a reload
 * or crash before a save lands would lose them. This keeps a text-only local
 * copy that is silently recovered ONLY when the server has no notes for the
 * survey — so it can never clobber server data.
 */

import type { SurveyNoteCard } from '../../types/survey';

const KEY = (surveyId: string): string => `survey-notes-backup:${surveyId}`;
const CARDS_KEY = (surveyId: string): string => `survey-notecards-backup:${surveyId}`;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days, matches FSR drafts

export function readNotesBackup(surveyId: string): string | null {
  try {
    const raw = localStorage.getItem(KEY(surveyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { text?: unknown; savedAt?: unknown };
    if (typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(KEY(surveyId));
      return null;
    }
    return typeof parsed.text === 'string' && parsed.text.trim() ? parsed.text : null;
  } catch {
    return null;
  }
}

export function writeNotesBackup(surveyId: string, text: string): void {
  try {
    if (!text.trim()) {
      localStorage.removeItem(KEY(surveyId));
      return;
    }
    localStorage.setItem(KEY(surveyId), JSON.stringify({ text, savedAt: Date.now() }));
  } catch {
    // Best-effort: quota errors are non-fatal (Firestore autosave is the
    // primary path; this is only a crash-recovery net).
  }
}

export function clearNotesBackup(surveyId: string): void {
  try {
    localStorage.removeItem(KEY(surveyId));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Ordered note-cards backup
// ---------------------------------------------------------------------------
//
// The mobile capture screen edits an ordered array of note cards with a 400ms
// debounce before the durable save lands. A reload/crash inside that window (or
// while the durable queue is still draining) would lose the freshly-typed card.
// This mirrors the free-text `notes` backup above: a text-only local copy,
// recovered ONLY when the server has no cards for the survey, so it can never
// clobber server data. Written synchronously so it is durable even on the
// unload path (where async IndexedDB work cannot be awaited).

/**
 * Read the backed-up note cards for a survey. Returns null when there is no
 * (non-expired, non-empty) backup. Malformed or stale entries are pruned.
 */
export function readNoteCardsBackup(surveyId: string): SurveyNoteCard[] | null {
  try {
    const raw = localStorage.getItem(CARDS_KEY(surveyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { cards?: unknown; savedAt?: unknown };
    if (typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(CARDS_KEY(surveyId));
      return null;
    }
    if (!Array.isArray(parsed.cards)) return null;
    const cards = (parsed.cards as SurveyNoteCard[]).filter(
      (c) => c && typeof c.text === 'string' && c.text.trim().length > 0,
    );
    return cards.length > 0 ? cards : null;
  } catch {
    return null;
  }
}

/**
 * Persist the ordered note cards for a survey. Empty edits clear the backup.
 * Best-effort and synchronous (safe on the unload path).
 */
export function writeNoteCardsBackup(surveyId: string, noteCards: SurveyNoteCard[]): void {
  try {
    const cards = noteCards.filter((c) => c && c.text.trim().length > 0);
    if (cards.length === 0) {
      localStorage.removeItem(CARDS_KEY(surveyId));
      return;
    }
    localStorage.setItem(CARDS_KEY(surveyId), JSON.stringify({ cards, savedAt: Date.now() }));
  } catch {
    // Best-effort: quota errors are non-fatal (the durable queue is the primary
    // path; this is only a crash-recovery net).
  }
}

export function clearNoteCardsBackup(surveyId: string): void {
  try {
    localStorage.removeItem(CARDS_KEY(surveyId));
  } catch {
    // ignore
  }
}

/**
 * Remove EVERY survey local backup (free-text notes + ordered note cards) for
 * ALL surveys. Called on logout / user-switch so a shared field tablet never
 * leaks one rep's in-progress capture into the next rep's session. Best-effort
 * and non-throwing.
 */
export function clearAllSurveyLocalBackups(): void {
  try {
    const prefixes = ['survey-notes-backup:', 'survey-notecards-backup:'];
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    // Collect first, then remove — removing during iteration shifts indices.
    for (const key of toRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // Best-effort: a quota/security error here must not block sign-out.
  }
}
